import { copyText } from '../../core/copy';
import { clear, el } from '../../core/dom';
import { makeBanner, makeChip, makeFieldHead, setChip, type StatusTone } from '../../core/shell';
import { decodeJwt, type JwtDecodeResult } from './parse';
import { SAMPLE_JWT } from './sample';

function statusMeta(result: JwtDecodeResult, idle: boolean): { text: string; tone: StatusTone } {
	if (idle) {
		return { text: 'Waiting for a token', tone: 'idle' };
	}
	if (!result.ok) {
		if (result.status === 'not-supported') {
			return { text: 'Not supported', tone: 'warn' };
		}
		return { text: 'Malformed', tone: 'error' };
	}
	if (result.status === 'expired') {
		return { text: 'Expired · signature not verified', tone: 'warn' };
	}
	return { text: 'Decoded · signature not verified', tone: 'ok' };
}

function syntaxNodes(json: string): DocumentFragment {
	const frag = document.createDocumentFragment();
	const re =
		/("(?:\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\btrue\b|\bfalse\b|\bnull\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g;
	let last = 0;
	let match: RegExpExecArray | null;
	while ((match = re.exec(json)) !== null) {
		if (match.index > last) {
			frag.appendChild(document.createTextNode(json.slice(last, match.index)));
		}
		const token = match[0];
		const span = document.createElement('span');
		if (/^"/.test(token)) {
			span.className = match[2] ? 'kb-tools-json__key' : 'kb-tools-json__string';
		} else if (/true|false/.test(token)) {
			span.className = 'kb-tools-json__bool';
		} else if (/null/.test(token)) {
			span.className = 'kb-tools-json__null';
		} else {
			span.className = 'kb-tools-json__number';
		}
		span.textContent = token;
		frag.appendChild(span);
		last = match.index + token.length;
	}
	if (last < json.length) {
		frag.appendChild(document.createTextNode(json.slice(last)));
	}
	return frag;
}

function emptyPre(): string {
	return '—';
}

export function mountJwtDecoder(root: HTMLElement): void {
	root.classList.add('kb-tools', 'kb-tools--jwt');
	root.querySelectorAll('.kb-tools__ssr-fallback').forEach((node) => node.remove());

	const textarea = el('textarea', {
		className: 'kb-tools__textarea kb-tools__textarea--tall',
		id: 'kb-jwt-input',
		rows: '8',
		spellcheck: 'false',
		autocomplete: 'off',
		autocapitalize: 'off',
		'aria-describedby': 'kb-jwt-status kb-jwt-privacy',
	}) as HTMLTextAreaElement;
	textarea.placeholder = 'eyJhbGciOiJ…  ·  eyJzdWIiOiJ…  ·  signature';

	const pasteSample = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Paste sample',
	]) as HTMLButtonElement;
	const decodeBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--primary' }, [
		'Decode',
	]) as HTMLButtonElement;
	const clearBtn = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--ghost' }, [
		'Clear',
	]) as HTMLButtonElement;

	const compose = el('div', { className: 'kb-tools__compose' }, [
		makeFieldHead(
			el('label', { className: 'kb-tools__label', for: 'kb-jwt-input' }, ['JWT (compact JWS)']),
			'⌘/Ctrl + Enter to decode'
		),
		textarea,
		el('div', { className: 'kb-tools__actions' }, [decodeBtn, pasteSample, clearBtn]),
	]);

	const chip = makeChip('kb-jwt-status');
	const statusRow = el('div', { className: 'kb-tools__status-row' }, [chip]);
	const warnings = el('ul', { className: 'kb-tools__warnings' });

	const headerPre = el('pre', { className: 'kb-tools-json', tabindex: '0' });
	const payloadPre = el('pre', { className: 'kb-tools-json', tabindex: '0' });
	const copyHeader = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--tiny' }, [
		'Copy',
	]) as HTMLButtonElement;
	const copyPayload = el('button', { type: 'button', className: 'kb-tools__btn kb-tools__btn--tiny' }, [
		'Copy',
	]) as HTMLButtonElement;

	const headerPanel = el('section', { className: 'kb-tools__panel kb-tools__panel--result' }, [
		el('div', { className: 'kb-tools__panel-head' }, [
			el('h3', { className: 'kb-tools__panel-title' }, ['Header']),
			copyHeader,
		]),
		headerPre,
	]);
	const payloadPanel = el('section', { className: 'kb-tools__panel kb-tools__panel--result' }, [
		el('div', { className: 'kb-tools__panel-head' }, [
			el('h3', { className: 'kb-tools__panel-title' }, ['Payload']),
			copyPayload,
		]),
		payloadPre,
	]);

	const claimsBody = el('div', { className: 'kb-tools__claims' });
	const claimsPanel = el('section', { className: 'kb-tools__panel kb-tools__panel--claims' }, [
		el('div', { className: 'kb-tools__panel-head' }, [
			el('h3', { className: 'kb-tools__panel-title' }, ['Claims helper']),
			el('span', { className: 'kb-tools__muted' }, ['iss · sub · aud · iat · nbf · exp']),
		]),
		claimsBody,
	]);

	const panels = el('div', { className: 'kb-tools__panels kb-tools__panels--jwt' }, [
		headerPanel,
		payloadPanel,
		claimsPanel,
	]);

	const results = el('div', { className: 'kb-tools__results' }, [
		statusRow,
		warnings,
		panels,
	]);

	const workspace = el('div', { className: 'kb-tools__workspace' }, [compose, results]);

	const privacy = el('p', { className: 'kb-tools__privacy', id: 'kb-jwt-privacy' }, [
		'Signature verification and key management are out of scope. A successful decode is not proof of authenticity.',
	]);

	clear(root);
	root.append(
		makeBanner(
			'Local',
			'Decoding stays in this browser. Tokens are not uploaded. Prefer samples or revoked test tokens over live credentials.'
		),
		workspace,
		privacy
	);

	let lastHeaderJson = '';
	let lastPayloadJson = '';
	let hasInteracted = false;

	const renderResult = (result: JwtDecodeResult, idle = false): void => {
		const meta = statusMeta(result, idle);
		setChip(chip, meta.text, meta.tone);

		clear(warnings);
		if (!idle) {
			for (const warning of result.warnings) {
				warnings.appendChild(el('li', {}, [warning]));
			}
			if (!result.ok && result.message) {
				warnings.appendChild(el('li', {}, [result.message]));
			}
		}

		clear(headerPre);
		clear(payloadPre);
		clear(claimsBody);
		lastHeaderJson = '';
		lastPayloadJson = '';
		panels.classList.toggle('kb-tools__panels--populated', result.ok);

		if (!result.ok) {
			headerPre.appendChild(document.createTextNode(emptyPre()));
			payloadPre.appendChild(document.createTextNode(emptyPre()));
			claimsBody.appendChild(
				el('p', { className: 'kb-tools__muted' }, [
					idle
						? 'Paste a token and decode to inspect standard claims with human-readable timestamps.'
						: 'Claims appear after a successful decode.',
				])
			);
			return;
		}

		lastHeaderJson = result.headerJson;
		lastPayloadJson = result.payloadJson;
		headerPre.appendChild(syntaxNodes(result.headerJson));
		payloadPre.appendChild(syntaxNodes(result.payloadJson));

		if (result.claims.length === 0) {
			claimsBody.appendChild(
				el('p', { className: 'kb-tools__muted' }, [
					'No standard claims (iss, sub, aud, iat, nbf, exp) were present.',
				])
			);
			return;
		}

		const list = el('dl', { className: 'kb-tools__claim-list' });
		for (const claim of result.claims) {
			list.appendChild(el('dt', {}, [claim.key]));
			const dd = el('dd', {});
			dd.appendChild(document.createTextNode(claim.display));
			if (claim.note) {
				dd.appendChild(el('span', { className: 'kb-tools__claim-note' }, [` · ${claim.note}`]));
			}
			list.appendChild(dd);
		}
		claimsBody.appendChild(list);
	};

	const runDecode = (): void => {
		hasInteracted = true;
		renderResult(decodeJwt(textarea.value), false);
	};

	pasteSample.addEventListener('click', () => {
		textarea.value = SAMPLE_JWT;
		textarea.focus();
		runDecode();
	});
	decodeBtn.addEventListener('click', runDecode);
	clearBtn.addEventListener('click', () => {
		textarea.value = '';
		hasInteracted = false;
		renderResult(
			{
				ok: false,
				status: 'malformed',
				message: '',
				warnings: [],
			},
			true
		);
		textarea.focus();
	});
	copyHeader.addEventListener('click', () => {
		if (lastHeaderJson) {
			void copyText(lastHeaderJson, copyHeader);
		}
	});
	copyPayload.addEventListener('click', () => {
		if (lastPayloadJson) {
			void copyText(lastPayloadJson, copyPayload);
		}
	});

	textarea.addEventListener('keydown', (event) => {
		if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
			event.preventDefault();
			runDecode();
		}
	});

	renderResult(
		{
			ok: false,
			status: 'malformed',
			message: '',
			warnings: [],
		},
		!hasInteracted
	);
}
